/**
 * MoveGenerator - Generates all legal moves for units
 *
 * Enumerates possible moves and orders them by priority for optimal alpha-beta pruning.
 */

import { Position, UnitCategory, TerrainType, CityLevel, UnitType, FactionType } from '../../../types';
import { StrategicPriority } from '../V3Types';
import { GameMap } from '../../../Map';
import { UNIT_DEFINITIONS, getUnitsForFaction as getUnitsForFactionFromDefs, isAirborneUnit, isAircraft } from '../../../unitDefinitions';
import {
  GameStateSnapshot,
  UnitSnapshot,
  CitySnapshot,
  GeneratedMove,
  MoveType,
  MoveFlags,
  FrontType,
  TERRAIN_DATA,
  UNIT_VALUES,
  positionToKey,
  hexDistance,
  getAdjacentPositions,
  isPositionOnCity,
  distanceToCity
} from '../V3Types';
import {
  getUnitsForFaction,
  getUnitAt,
  isValidPosition,
  calculateExpectedDamage
} from './GameStateSnapshot';

/**
 * Generate all legal moves for a unit
 */
export function generateMovesForUnit(
  snapshot: GameStateSnapshot,
  unit: UnitSnapshot,
  map: GameMap
): GeneratedMove[] {
  const moves: GeneratedMove[] = [];

  // Skip if unit has no actions remaining
  if (unit.movementRemaining <= 0 && unit.hasAttacked) {
    return moves;
  }

  // 1. Generate attack moves (if not already attacked)
  if (!unit.hasAttacked) {
    const attackMoves = generateAttackMoves(snapshot, unit);
    moves.push(...attackMoves);
  }

  // 2. Generate movement moves
  if (unit.movementRemaining > 0) {
    const movementMoves = generateMovementMoves(snapshot, unit, map);
    moves.push(...movementMoves);

    // 3. Generate move-and-attack combinations
    if (!unit.hasAttacked) {
      const moveAttackMoves = generateMoveAndAttackMoves(snapshot, unit, map);
      moves.push(...moveAttackMoves);
    }
  }

  // 4. Generate special moves (hunker, etc.)
  const specialMoves = generateSpecialMoves(snapshot, unit);
  moves.push(...specialMoves);

  // 5. Only add PASS if there are truly no other options
  // The AI should always try to do something useful with its units
  if (moves.length === 0) {
    moves.push({
      unitId: unit.id,
      type: MoveType.PASS,
      from: unit.position,
      priority: -1000, // Very low priority - only use if nothing else available
      flags: createEmptyFlags()
    });
  }

  return moves;
}

/**
 * Generate attack-only moves
 */
function generateAttackMoves(
  snapshot: GameStateSnapshot,
  unit: UnitSnapshot
): GeneratedMove[] {
  const moves: GeneratedMove[] = [];
  const enemies = getUnitsForFaction(snapshot, snapshot.enemyFaction);

  for (const enemy of enemies) {
    const distance = hexDistance(unit.position, enemy.position);

    // Check if enemy is in range
    if (distance <= unit.range) {
      const expectedDamage = calculateExpectedDamage(unit, enemy);
      const canKill = expectedDamage >= enemy.health;
      const priority = calculateAttackPriority(unit, enemy, expectedDamage, canKill, snapshot);

      moves.push({
        unitId: unit.id,
        type: MoveType.ATTACK,
        from: unit.position,
        targetId: enemy.id,
        priority,
        flags: {
          isKillingBlow: canKill,
          threatensCity: false,
          capturesCity: false,
          retreatsToSafety: false,
          supportsAlly: isDefendingAlly(unit, enemy, snapshot),
          improvesPosition: false
        }
      });
    }
  }

  return moves;
}

/**
 * Check if a unit should stay on a city for garrison/defense duty
 * Returns the city if the unit should be restricted, null otherwise
 *
 * GARRISON units: MUST stay on city (strictest)
 * DEFENSE units: Should stay on city if it's being captured or has enemy units on it
 */
function getDefendedCityForUnit(
  unit: UnitSnapshot,
  snapshot: GameStateSnapshot
): { city: CitySnapshot; strict: boolean } | null {
  const unitFrontType = snapshot.unitFrontTypes?.get(unit.id);

  // Check both GARRISON and DEFENSE assignments
  if (unitFrontType !== FrontType.GARRISON && unitFrontType !== FrontType.DEFENSE) {
    return null;
  }

  const objective = snapshot.unitObjectives?.get(unit.id);
  if (!objective) return null;

  const ourCities = snapshot.cities.filter(c => c.faction === snapshot.currentFaction);
  const defendedCity = ourCities.find(c =>
    isPositionOnCity(objective, c) ||
    (c.centerPosition.x === objective.x && c.centerPosition.y === objective.y)
  );

  if (!defendedCity) return null;

  // Only apply restrictions if unit is currently ON the city
  if (!isPositionOnCity(unit.position, defendedCity)) {
    return null;
  }

  // GARRISON is always strict - must stay on city
  if (unitFrontType === FrontType.GARRISON) {
    return { city: defendedCity, strict: true };
  }

  // DEFENSE is strict only if city is being captured or has enemies on it
  if (unitFrontType === FrontType.DEFENSE) {
    const enemyUnits = getUnitsForFaction(snapshot, snapshot.enemyFaction);
    const enemiesOnCity = enemyUnits.some(e => isPositionOnCity(e.position, defendedCity));
    const isBeingCaptured = defendedCity.isBeingCaptured;

    if (isBeingCaptured || enemiesOnCity) {
      return { city: defendedCity, strict: true };
    }

    // City not in immediate danger - defense units have more freedom
    return { city: defendedCity, strict: false };
  }

  return null;
}

/**
 * Generate movement-only moves
 */
function generateMovementMoves(
  snapshot: GameStateSnapshot,
  unit: UnitSnapshot,
  map: GameMap
): GeneratedMove[] {
  const moves: GeneratedMove[] = [];
  const reachable = getReachablePositions(snapshot, unit, map);

  // Check if this unit is GARRISON/DEFENSE-assigned and on a defended city
  const defendedCityInfo = getDefendedCityForUnit(unit, snapshot);

  for (const pos of reachable) {
    // DEFENSE/GARRISON ENFORCEMENT: If unit is on defended city with strict mode,
    // ONLY allow moves that stay on city OR move toward nearby enemies (for attack)
    if (defendedCityInfo && defendedCityInfo.strict) {
      const destOnCity = isPositionOnCity(pos, defendedCityInfo.city);

      if (!destOnCity) {
        // Check if this is moving toward a nearby enemy threat
        const enemyUnits = getUnitsForFaction(snapshot, snapshot.enemyFaction);
        const nearbyEnemies = enemyUnits.filter(e =>
          distanceToCity(e.position, defendedCityInfo.city) <= 4 ||
          hexDistance(e.position, unit.position) <= unit.range + 2
        );

        let isMovingTowardEnemy = false;
        for (const enemy of nearbyEnemies) {
          const currentDist = hexDistance(unit.position, enemy.position);
          const newDist = hexDistance(pos, enemy.position);
          if (newDist < currentDist && newDist <= unit.range + 1) {
            isMovingTowardEnemy = true;
            break;
          }
        }

        if (!isMovingTowardEnemy) {
          // Skip this move - unit cannot leave defended city without attacking
          continue;
        }
      }
    }

    const priority = calculateMovePriority(unit, pos, snapshot, map);
    const flags = calculateMoveFlags(unit, pos, snapshot, map);

    moves.push({
      unitId: unit.id,
      type: MoveType.MOVE,
      from: unit.position,
      to: pos,
      priority,
      flags
    });
  }

  return moves;
}

/**
 * Generate move-then-attack combinations
 */
function generateMoveAndAttackMoves(
  snapshot: GameStateSnapshot,
  unit: UnitSnapshot,
  map: GameMap
): GeneratedMove[] {
  const moves: GeneratedMove[] = [];
  const reachable = getReachablePositions(snapshot, unit, map);
  const enemies = getUnitsForFaction(snapshot, snapshot.enemyFaction);

  // Check if this unit is GARRISON/DEFENSE-assigned and on a defended city
  const defendedCityInfo = getDefendedCityForUnit(unit, snapshot);

  for (const pos of reachable) {
    // DEFENSE/GARRISON ENFORCEMENT: If on defended city with strict mode,
    // only allow move-attack if:
    // 1. Destination stays on city, OR
    // 2. Target enemy is threatening the city (within 4 tiles)
    if (defendedCityInfo && defendedCityInfo.strict) {
      const destOnCity = isPositionOnCity(pos, defendedCityInfo.city);

      // Pre-filter: if destination is off city, only consider enemies near city
      if (!destOnCity) {
        // Only generate move-attacks against enemies near the defended city
        const validEnemies = enemies.filter(e =>
          distanceToCity(e.position, defendedCityInfo.city) <= 4
        );

        for (const enemy of validEnemies) {
          const distanceFromNewPos = hexDistance(pos, enemy.position);

          if (distanceFromNewPos <= unit.range) {
            const expectedDamage = calculateExpectedDamage(unit, enemy);
            const canKill = expectedDamage >= enemy.health;
            const priority = calculateMoveAttackPriority(unit, pos, enemy, expectedDamage, canKill, snapshot, map);

            moves.push({
              unitId: unit.id,
              type: MoveType.MOVE_AND_ATTACK,
              from: unit.position,
              to: pos,
              targetId: enemy.id,
              priority: priority + 200, // Bonus for defending city
              flags: {
                isKillingBlow: canKill,
                threatensCity: isThreatToCity(pos, snapshot),
                capturesCity: false,
                retreatsToSafety: false,
                supportsAlly: isDefendingAlly(unit, enemy, snapshot),
                improvesPosition: isPositionImprovement(unit, pos, snapshot, map)
              }
            });
          }
        }
        continue; // Skip normal processing for this position
      }
    }

    // Normal processing for non-defended units OR defended units staying on city
    for (const enemy of enemies) {
      const distanceFromNewPos = hexDistance(pos, enemy.position);

      // Can attack this enemy from the new position?
      if (distanceFromNewPos <= unit.range) {
        const expectedDamage = calculateExpectedDamage(unit, enemy);
        const canKill = expectedDamage >= enemy.health;
        const priority = calculateMoveAttackPriority(unit, pos, enemy, expectedDamage, canKill, snapshot, map);

        moves.push({
          unitId: unit.id,
          type: MoveType.MOVE_AND_ATTACK,
          from: unit.position,
          to: pos,
          targetId: enemy.id,
          priority,
          flags: {
            isKillingBlow: canKill,
            threatensCity: isThreatToCity(pos, snapshot),
            capturesCity: false,
            retreatsToSafety: false,
            supportsAlly: isDefendingAlly(unit, enemy, snapshot),
            improvesPosition: isPositionImprovement(unit, pos, snapshot, map)
          }
        });
      }
    }
  }

  return moves;
}

/**
 * Generate special moves (hunker, heal, repair, etc.)
 */
function generateSpecialMoves(
  snapshot: GameStateSnapshot,
  unit: UnitSnapshot
): GeneratedMove[] {
  const moves: GeneratedMove[] = [];

  // Hunker down - ONLY when defending a city under threat
  // Hunker is generally not useful unless holding a defensive position
  if (unit.movementRemaining > 0 || !unit.hasAttacked) {
    const ourCities = snapshot.cities.filter(c => c.faction === snapshot.currentFaction);
    const isOnOurCity = ourCities.some(c => isPositionOnCity(unit.position, c));

    // Only consider hunker if on one of our cities
    if (isOnOurCity) {
      const enemies = getUnitsForFaction(snapshot, snapshot.enemyFaction);
      const nearbyEnemies = enemies.filter(e => hexDistance(unit.position, e.position) <= 4);

      // Only hunker if city is actually threatened AND we're low health
      if (nearbyEnemies.length > 0) {
        const healthPercent = unit.health / unit.maxHealth;
        if (healthPercent < 0.5) {
          // Low priority - only hunker as last resort when defending damaged
          moves.push({
            unitId: unit.id,
            type: MoveType.HUNKER,
            from: unit.position,
            priority: 20, // Low priority - prefer other actions
            flags: {
              isKillingBlow: false,
              threatensCity: false,
              capturesCity: false,
              retreatsToSafety: true,
              supportsAlly: false,
              improvesPosition: false
            }
          });
        }
      }
    }
  }

  // Medic healing
  if (unit.category === UnitCategory.MEDICS && !unit.hasAttacked) {
    const allies = getUnitsForFaction(snapshot, snapshot.currentFaction);
    const woundedAllies = allies.filter(a =>
      a.id !== unit.id &&
      a.health < a.maxHealth &&
      hexDistance(unit.position, a.position) <= 1 &&
      a.category === UnitCategory.INFANTRY
    );

    for (const ally of woundedAllies) {
      const healthMissing = ally.maxHealth - ally.health;
      const priority = 100 + healthMissing * 5 + (UNIT_VALUES[ally.category] / 10);

      moves.push({
        unitId: unit.id,
        type: MoveType.HEAL,
        from: unit.position,
        targetId: ally.id,
        priority,
        flags: {
          isKillingBlow: false,
          threatensCity: false,
          capturesCity: false,
          retreatsToSafety: false,
          supportsAlly: true,
          improvesPosition: false
        }
      });
    }
  }

  // Engineer repair
  if (unit.category === UnitCategory.ENGINEERS && !unit.hasAttacked) {
    const allies = getUnitsForFaction(snapshot, snapshot.currentFaction);
    const damagedVehicles = allies.filter(a =>
      a.id !== unit.id &&
      a.health < a.maxHealth &&
      hexDistance(unit.position, a.position) <= 1 &&
      (a.category === UnitCategory.ARMOR || a.category === UnitCategory.ARTILLERY)
    );

    for (const vehicle of damagedVehicles) {
      const healthMissing = vehicle.maxHealth - vehicle.health;
      const priority = 100 + healthMissing * 5 + (UNIT_VALUES[vehicle.category] / 10);

      moves.push({
        unitId: unit.id,
        type: MoveType.REPAIR,
        from: unit.position,
        targetId: vehicle.id,
        priority,
        flags: {
          isKillingBlow: false,
          threatensCity: false,
          capturesCity: false,
          retreatsToSafety: false,
          supportsAlly: true,
          improvesPosition: false
        }
      });
    }
  }

  // Transport boarding - Infantry can board transports (TRANSPORT or HALFTRACK)
  if (unit.category === UnitCategory.INFANTRY && unit.movementRemaining > 0) {
    const allies = getUnitsForFaction(snapshot, snapshot.currentFaction);
    const nearbyTransports = allies.filter(a =>
      a.id !== unit.id &&
      (a.category === UnitCategory.TRANSPORT || a.category === UnitCategory.HALFTRACK) &&
      hexDistance(unit.position, a.position) <= 1 &&
      !a.isCarryingUnit  // Transport not already full
    );

    for (const transport of nearbyTransports) {
      // Calculate priority based on distance to nearest objective
      let priority = 150;  // Base priority for boarding

      // Check if there are distant objectives that make transport valuable
      const targetCities = snapshot.cities.filter(c =>
        c.faction === snapshot.enemyFaction || c.faction === null
      );
      if (targetCities.length > 0) {
        const closestCity = targetCities.reduce((closest, city) => {
          const dist = distanceToCity(unit.position, city);
          const closestDist = distanceToCity(unit.position, closest);
          return dist < closestDist ? city : closest;
        });
        const distToTarget = distanceToCity(unit.position, closestCity);

        // Higher priority to board if target is far away
        if (distToTarget > 10) {
          priority += 100;  // Very valuable to board for long journeys
        } else if (distToTarget > 5) {
          priority += 50;   // Moderately valuable
        }
      }

      moves.push({
        unitId: unit.id,
        type: MoveType.BOARD_TRANSPORT,
        from: unit.position,
        targetId: transport.id,
        priority,
        flags: {
          isKillingBlow: false,
          threatensCity: false,
          capturesCity: false,
          retreatsToSafety: false,
          supportsAlly: false,
          improvesPosition: true
        }
      });
    }
  }

  // Disembark from transport - transport carrying units should unload near objectives
  if ((unit.category === UnitCategory.TRANSPORT || unit.category === UnitCategory.HALFTRACK) &&
      unit.isCarryingUnit && unit.movementRemaining > 0) {
    // Find adjacent empty hexes to disembark into
    const adjacentPositions = getAdjacentPositions(unit.position);
    const emptyAdjacent = adjacentPositions.filter(pos =>
      isValidPosition(snapshot, pos) &&
      !snapshot.occupiedPositions.has(positionToKey(pos))
    );

    // Check if we're near a target city (should disembark)
    const targetCities = snapshot.cities.filter(c =>
      c.faction === snapshot.enemyFaction || c.faction === null
    );

    for (const pos of emptyAdjacent) {
      let priority = 50;  // Base disembark priority

      // High priority to disembark if we're near/on a target city
      for (const city of targetCities) {
        const distToCity = distanceToCity(pos, city);
        if (distToCity <= 1) {
          // Disembarking right next to/on a city tile is very valuable
          priority += 200;
          break;
        } else if (distToCity <= 3) {
          priority += 100;
        }
      }

      moves.push({
        unitId: unit.id,
        type: MoveType.DISEMBARK,
        from: unit.position,
        to: pos,
        priority,
        flags: {
          isKillingBlow: false,
          threatensCity: targetCities.length > 0 && isPositionOnCity(pos, targetCities[0]),
          capturesCity: false,
          retreatsToSafety: false,
          supportsAlly: false,
          improvesPosition: true
        }
      });
    }
  }

  return moves;
}

/**
 * Generate production moves (build units, upgrade cities)
 *
 * SMART PRODUCTION STRATEGY:
 * - Save up for better units when appropriate (tanks > infantry spam)
 * - Maintain diverse army composition
 * - Only spam cheap units when desperate
 * - EXPANSION MODE: Prioritize recon/transport and city upgrades!
 *
 * IMPORTANT: Respects game rules:
 * - City capacity (unitsProduced + unitsInTraining < maxUnits)
 * - City level requirements for unit types (category-based)
 */
export function generateProductionMoves(
  snapshot: GameStateSnapshot,
  _map: GameMap,  // Reserved for future terrain-based production decisions
  strategicPriority?: StrategicPriority
): GeneratedMove[] {
  const moves: GeneratedMove[] = [];
  const resources = snapshot.resources.get(snapshot.currentFaction) || 0;
  const ownedCities = snapshot.cities.filter(c => c.faction === snapshot.currentFaction);
  const ourUnits = getUnitsForFaction(snapshot, snapshot.currentFaction);
  const enemyUnits = getUnitsForFaction(snapshot, snapshot.enemyFaction);

  // Analyze current situation
  const totalUnits = ourUnits.length;
  const enemyCount = enemyUnits.length;
  const composition = analyzeComposition(ourUnits);

  // Check if we're in EXPANSION mode - critical for production strategy
  const isExpansionMode = strategicPriority === StrategicPriority.EXPANSION;
  if (isExpansionMode) {
    console.log(`[V3 MoveGen] EXPANSION MODE - prioritizing recon/transport and city upgrades`);
  }

  // === TRANSPORT PRODUCTION LIMITS ===
  // Don't build transports when existing ones aren't being used
  const existingTransports = ourUnits.filter(u =>
    u.category === UnitCategory.TRANSPORT || u.category === UnitCategory.HALFTRACK
  );
  const unusedTransports = existingTransports.filter(t => !t.isCarryingUnit);
  const transportableInfantry = ourUnits.filter(u =>
    u.category === UnitCategory.INFANTRY && !u.towingUnit // Infantry not being towed/carried
  );
  // We have excess transports if: unused transports >= transportable infantry
  const hasExcessTransports = unusedTransports.length >= transportableInfantry.length && unusedTransports.length > 0;
  if (hasExcessTransports) {
    console.log(`[V3 MoveGen] TRANSPORT LIMIT: ${unusedTransports.length} unused transports, ${transportableInfantry.length} transportable infantry - will penalize transport production`);
  }

  // Determine production strategy based on situation
  const desperateForUnits = totalUnits < 3;
  const needMoreUnits = totalUnits < 8;
  const enemyHasMoreUnits = enemyCount > totalUnits + 2;
  const hasFactory = ownedCities.some(c => c.level >= CityLevel.FACTORY);

  // === LATE GAME DETECTION ===
  // When we have multiple factories and good resources, prioritize ARMOR over infantry
  // Infantry gets overwhelmed by armor waves in late game
  const factoryCount = ownedCities.filter(c => c.level >= CityLevel.FACTORY).length;
  const isLateGame = factoryCount >= 2 && resources >= 40 && totalUnits >= 8;
  if (isLateGame) {
    console.log(`[V3 MoveGen] LATE GAME detected: ${factoryCount} factories, ${resources} resources - prioritizing ARMOR`);
  }

  // === CHECK IF ANY CITIES NEED GARRISON ===
  // If cities are undefended or upgrading with enemies nearby, prioritize building defenders!
  const enemyCities = snapshot.cities.filter(c => c.faction === snapshot.enemyFaction);
  let needsGarrisonUnits = false;
  for (const city of ownedCities) {
    const defendersOnCity = ourUnits.filter(u => isPositionOnCity(u.position, city)).length;
    const enemiesNearby = enemyUnits.filter(u => distanceToCity(u.position, city) <= 6).length;
    const isFrontline = enemyCities.some(ec => hexDistance(city.centerPosition, ec.centerPosition) <= 10);

    // City needs garrison if: upgrading with few defenders, or frontline with few defenders
    if ((city.isUpgrading && defendersOnCity < 1) ||
        (isFrontline && defendersOnCity < 1 && enemiesNearby > 0) ||
        (enemiesNearby >= 2 && defendersOnCity < 2)) {
      needsGarrisonUnits = true;
      console.log(`[V3 MoveGen] City ${city.name} needs garrison! (defenders=${defendersOnCity}, enemies=${enemiesNearby}, upgrading=${city.isUpgrading})`);
      break;
    }
  }

  // === CHECK IF ANY CITIES NEED GARRISON ARTILLERY ===
  // Artillery is excellent for city defense - each frontline/threatened city should have one
  // Only prioritize if we have resources and aren't in a desperate situation
  let citiesNeedingArtillery: string[] = [];
  if (hasFactory && resources >= 20 && !desperateForUnits) {
    for (const city of ownedCities) {
      const artilleryNearCity = ourUnits.filter(u =>
        u.category === UnitCategory.ARTILLERY &&
        distanceToCity(u.position, city) <= 4
      ).length;
      const enemiesNearby = enemyUnits.filter(u => distanceToCity(u.position, city) <= 6).length;
      const isFrontline = enemyCities.some(ec => hexDistance(city.centerPosition, ec.centerPosition) <= 10);

      // Frontline city or city with nearby enemies needs artillery for defense
      if ((isFrontline || enemiesNearby > 0) && artilleryNearCity === 0) {
        citiesNeedingArtillery.push(city.name);
      }
    }
    if (citiesNeedingArtillery.length > 0) {
      console.log(`[V3 MoveGen] Cities needing garrison artillery: ${citiesNeedingArtillery.join(', ')}`);
    }
  }

  // SAVING STRATEGY: Don't always spend all resources
  // Save up for tanks/artillery if we have a factory and aren't desperate
  // BUT if we need garrison units, don't save - build infantry NOW!
  const shouldSaveForBetterUnits = hasFactory && !desperateForUnits && !enemyHasMoreUnits && !needsGarrisonUnits && resources < 25;

  // Minimum resources to keep in reserve (for emergencies) - keep it simple
  const reserveResources = desperateForUnits || needsGarrisonUnits ? 0 : 5;

  // Don't build anything if saving for better units
  if (shouldSaveForBetterUnits) {
    console.log(`[V3 MoveGen] Saving resources (${resources}) for better units - skipping cheap production`);
    // Still allow upgrades though
  }

  for (const city of ownedCities) {
    // === PRODUCTION BLOCKERS ===
    // Check if city cannot produce units due to game mechanics

    // 1. City is upgrading - can't produce units during upgrade
    if (city.isUpgrading) {
      console.log(`[V3 MoveGen] City ${city.name}: upgrading - skipping all production`);
      continue;  // Skip this city entirely (can't build OR upgrade during an upgrade)
    }

    // 2. Enemy units ON the city tiles - blocks production
    const enemyOnCity = enemyUnits.some(u => isPositionOnCity(u.position, city));
    if (enemyOnCity) {
      console.log(`[V3 MoveGen] City ${city.name}: enemy units on city - skipping all production`);
      continue;  // Skip this city entirely
    }

    // CHECK CAPACITY: Only skip BUILDING units, NOT upgrades
    const currentCapacity = city.unitsProduced + city.unitsInTraining;
    const cityAtCapacity = currentCapacity >= city.maxUnits;

    if (cityAtCapacity) {
      console.log(`[V3 MoveGen] City ${city.name}: at capacity (${currentCapacity}/${city.maxUnits}) - skipping unit production, but can still upgrade`);
    }

    // Only generate BUILD moves if city is NOT at capacity
    if (!cityAtCapacity) {
      // Get available units for this city level (using proper category-based logic)
      const availableUnits = getAvailableUnitsForCity(city, snapshot.currentFaction, snapshot.campaign);

    // Sort units by cost (expensive first) to prioritize better units
    const sortedUnits = [...availableUnits].sort((a, b) => {
      const costA = UNIT_DEFINITIONS[a]?.cost || 0;
      const costB = UNIT_DEFINITIONS[b]?.cost || 0;
      return costB - costA;  // Expensive first
    });

    for (const unitType of sortedUnits) {
      const def = UNIT_DEFINITIONS[unitType];
      const affordableWithReserve = def.cost <= (resources - reserveResources);
      const affordableAtAll = def.cost <= resources;

      // Skip if we can't afford it at all
      if (!affordableAtAll) continue;

      // Skip cheap units if we're saving for better ones (unless desperate)
      if (shouldSaveForBetterUnits && def.cost < 20) continue;

      // Calculate priority based on army composition needs
      let priority = calculateBuildPriority(unitType, snapshot, isLateGame);

      // Penalize if we can't afford it while keeping reserves
      if (!affordableWithReserve) {
        priority -= 30;
      }

      // Adjust priority based on situation
      if (desperateForUnits) {
        // When desperate, boost ALL unit production
        priority += 150;
      } else if (needMoreUnits || enemyHasMoreUnits) {
        priority += 50;
      }

      // Prefer more expensive/powerful units when we have resources
      if (def.cost >= 20 && resources >= 30) {
        priority += 40;  // Prefer tanks/artillery when we can afford them
      }

      // Strong preference for tanks if we have none
      if (def.category === UnitCategory.ARMOR && composition.armor < 0.1) {
        priority += 80;
      }

      // Preference for artillery if we have none
      if (def.category === UnitCategory.ARTILLERY && composition.artillery < 0.1) {
        priority += 60;
      }

      // === GARRISON ARTILLERY REPLACEMENT ===
      // If frontline cities are missing their garrison artillery, prioritize building replacements
      // But only if we're not desperate and have reasonable resources
      if (def.category === UnitCategory.ARTILLERY && citiesNeedingArtillery.length > 0) {
        // Scale priority by how many cities need artillery (more urgent with more cities exposed)
        const artilleryBoost = Math.min(150, citiesNeedingArtillery.length * 75);
        priority += artilleryBoost;
        console.log(`[V3 MoveGen] GARRISON ARTILLERY: Boosting ${unitType} priority by ${artilleryBoost} (${citiesNeedingArtillery.length} cities need artillery)`);
      }

      // === LATE GAME ARMOR PRIORITY ===
      // When we have multiple factories and resources, armor beats infantry
      // The player will switch to armor waves - we need to match them
      if (isLateGame && !needsGarrisonUnits) {
        if (def.category === UnitCategory.ARMOR) {
          priority += 200;  // STRONG preference for armor in late game
          console.log(`[V3 MoveGen] LATE GAME: Boosting ${unitType} priority by 200 (armor dominates late game)`);
        }
        // Also boost artillery - great for anti-armor
        if (def.category === UnitCategory.ARTILLERY) {
          priority += 150;  // Artillery is excellent against armor
          console.log(`[V3 MoveGen] LATE GAME: Boosting ${unitType} priority by 150 (artillery for anti-armor)`);
        }
        // Penalize infantry in late game - they get overwhelmed by armor
        if (def.category === UnitCategory.INFANTRY) {
          priority -= 100;  // Infantry is weak vs armor waves
        }
      }

      // EXPANSION MODE: Strong preference for recon and transport units!
      // These units capture cities faster (recon is fast, transports carry infantry)
      if (isExpansionMode && !needsGarrisonUnits) {
        if (def.category === UnitCategory.RECONNAISSANCE) {
          priority += 150;  // VERY high bonus - recon is CRITICAL for fast expansion
          console.log(`[V3 MoveGen] EXPANSION: Boosting ${unitType} priority by 150 (recon for fast capture)`);
        }
        if (def.category === UnitCategory.TRANSPORT || def.category === UnitCategory.HALFTRACK) {
          priority += 120;  // High bonus - transports speed up infantry
          console.log(`[V3 MoveGen] EXPANSION: Boosting ${unitType} priority by 120 (transport for mobility)`);
        }
        // Don't boost infantry as much - we want mobility units
        if (def.category === UnitCategory.INFANTRY) {
          priority -= 30;  // Slight penalty - prefer recon over infantry spam
        }
      }

      // GARRISON URGENCY: When cities need defenders, MASSIVELY boost infantry production!
      if (needsGarrisonUnits) {
        if (def.category === UnitCategory.INFANTRY) {
          priority += 300;  // HUGE boost - need infantry to defend cities NOW!
          console.log(`[V3 MoveGen] GARRISON URGENT: Boosting ${unitType} priority by 300 (need defenders!)`);
        }
        // Also slight boost for recon (they can hold cities too)
        if (def.category === UnitCategory.RECONNAISSANCE) {
          priority += 100;
        }
      }

      // === TRANSPORT PRODUCTION PENALTY ===
      // Don't build more transports when existing ones aren't being used!
      if ((def.category === UnitCategory.TRANSPORT || def.category === UnitCategory.HALFTRACK) && hasExcessTransports) {
        priority -= 500;  // MASSIVE penalty - use existing transports first!
        console.log(`[V3 MoveGen] TRANSPORT PENALTY: Heavily penalizing ${unitType} - have ${unusedTransports.length} unused transports`);
      }

      moves.push({
        unitId: 'production',
        type: MoveType.BUILD,
        from: city.centerPosition,
        unitType,
        cityName: city.name,
        priority,
        flags: createEmptyFlags()
      });
      }
    } // End of !cityAtCapacity block for BUILD moves

    // City upgrade - HIGH PRIORITY for expansion (transports/recon help capture cities faster)
    if (city.level < CityLevel.AIRBASE) {
      const nextLevel = city.level + 1 as CityLevel;
      const upgradeCost = nextLevel === CityLevel.FACTORY ? 25 : 50;  // Factory=25, Airbase=50

      if (resources >= upgradeCost) {
        // Higher base priority - upgrades unlock transports/recon which speed up expansion
        let upgradePriority = 120;  // Increased from 50 - upgrades are strategically valuable

        // === EXPANSION MODE BONUSES ===
        // In EXPANSION mode, upgrades are valuable because they unlock recon/transports
        // But prioritize MORE once we have 2+ cities (10+ income) - can afford both units AND upgrades
        const citiesOwned = ownedCities.length;
        if (isExpansionMode) {
          if (citiesOwned >= 2) {
            // With 2+ cities (10+ income), strongly prioritize upgrades
            upgradePriority += 150;
            console.log(`[V3 MoveGen] EXPANSION: Boosting upgrade priority for ${city.name} by 150 (have ${citiesOwned} cities)`);
          } else {
            // With 1 city, moderate bonus - still want to build units for expansion
            upgradePriority += 50;
          }
        }

        // === SITUATIONAL BONUSES ===

        // First factory is CRITICAL - unlocks tanks AND transports/halftracks!
        if (nextLevel === CityLevel.FACTORY && !hasFactory) {
          // Higher priority with more cities since we can afford it faster
          upgradePriority = citiesOwned >= 2 ? 300 : 200;
          if (isExpansionMode) upgradePriority += 50;
          console.log(`[V3 MoveGen] First FACTORY upgrade for ${city.name}: priority=${upgradePriority} (unlocks recon/transport!)`);
        }

        // Good economy situation - upgrade is smart investment
        const hasGoodEconomy = resources >= upgradeCost + 15;  // Can afford upgrade + have buffer
        const armyIsStable = totalUnits >= 5 && !enemyHasMoreUnits;

        if (armyIsStable && hasGoodEconomy) {
          upgradePriority += 60;  // Strong incentive to upgrade when stable
        } else if (totalUnits >= 3 && hasGoodEconomy) {
          upgradePriority += 40;  // Moderate incentive even with smaller army
        }

        // Airbase upgrade is valuable for aircraft
        if (nextLevel === CityLevel.AIRBASE) {
          upgradePriority += 30;  // Aircraft are powerful
        }

        // BIG BONUS: If this city is at capacity, upgrading is excellent use of resources!
        // Can't build more units anyway, so might as well improve the city
        if (cityAtCapacity) {
          upgradePriority += 80;  // Strong incentive to upgrade when can't build
          console.log(`[V3 MoveGen] City ${city.name}: at capacity, boosting upgrade priority to ${upgradePriority}`);
        }

        // Check if ALL cities are at capacity - upgrading becomes critical
        const allCitiesAtCapacity = ownedCities.every(c =>
          (c.unitsProduced + c.unitsInTraining) >= c.maxUnits
        );
        if (allCitiesAtCapacity) {
          upgradePriority += 60;  // Extra bonus when literally can't build anywhere
        }

        // === SITUATIONAL PENALTIES ===

        // Only penalize upgrade when truly desperate for units (< 3) AND can build
        if (desperateForUnits && !cityAtCapacity) {
          upgradePriority -= 80;  // Reduced penalty - upgrades still valuable
        }

        // Slight penalty if enemy has significant advantage (but only if we can build)
        if (enemyHasMoreUnits && enemyCount > totalUnits + 4 && !allCitiesAtCapacity) {
          upgradePriority -= 40;  // Reduced penalty
        }

        // GARRISON URGENCY: If cities need defenders, DON'T upgrade - build infantry instead!
        if (needsGarrisonUnits && !cityAtCapacity) {
          upgradePriority -= 200;  // MASSIVE penalty - need to build defenders, not upgrade!
          console.log(`[V3 MoveGen] GARRISON URGENT: Penalizing upgrade of ${city.name} - need defenders first!`);
        }

        // === UPGRADE SAFETY CHECK ===
        // Apply priority penalties for undefended cities (but don't hard-block)
        // The garrison system should keep units on cities - this is just a backup
        const defendersOnCity = ourUnits.filter(u =>
          isPositionOnCity(u.position, city)
        ).length;

        // Count enemy threats within 6 tiles
        const enemyThreats = enemyUnits.filter(u =>
          distanceToCity(u.position, city) <= 6
        ).length;

        // Penalize upgrading undefended cities when enemies are nearby
        if (defendersOnCity < 1 && enemyThreats > 0) {
          upgradePriority -= 100;  // Penalty but don't block - garrison system should handle defense
          console.log(`[V3 MoveGen] Penalizing upgrade of ${city.name}: no defender ON city with ${enemyThreats} enemies nearby`);
        }

        // Only add if priority is still reasonable
        if (upgradePriority > 0) {
          moves.push({
            unitId: 'production',
            type: MoveType.UPGRADE_CITY,
            from: city.centerPosition,
            cityName: city.name,
            upgradeLevel: nextLevel,
            priority: upgradePriority,
            flags: createEmptyFlags()
          });
        }
      }
    }
  }

  return moves;
}

/**
 * Get all positions reachable by a unit
 */
function getReachablePositions(
  snapshot: GameStateSnapshot,
  unit: UnitSnapshot,
  map: GameMap
): Position[] {
  const reachable: Position[] = [];
  const visited = new Set<string>();
  const queue: { pos: Position; remaining: number }[] = [
    { pos: unit.position, remaining: unit.movementRemaining }
  ];

  visited.add(positionToKey(unit.position));

  while (queue.length > 0) {
    const { pos, remaining } = queue.shift()!;

    if (remaining <= 0) continue;

    const neighbors = getAdjacentPositions(pos);
    for (const neighbor of neighbors) {
      const key = positionToKey(neighbor);

      if (visited.has(key)) continue;
      if (!isValidPosition(snapshot, neighbor)) continue;

      // Get terrain cost
      const tile = map.getTile(neighbor.x, neighbor.y);
      if (!tile) continue;

      const terrainCost = getTerrainMovementCost(tile.terrain);
      if (remaining < terrainCost) continue;

      // Check if occupied by friendly unit (can't move through)
      const occupant = getUnitAt(snapshot, neighbor);
      if (occupant && occupant.faction === snapshot.currentFaction) continue;

      // Can move here if empty or enemy (for attack destination)
      if (!occupant) {
        reachable.push(neighbor);
      }

      visited.add(key);
      queue.push({ pos: neighbor, remaining: remaining - terrainCost });
    }
  }

  return reachable;
}

/**
 * Get terrain movement cost
 */
function getTerrainMovementCost(terrain: TerrainType): number {
  return TERRAIN_DATA[terrain]?.movementCost ?? 1;
}

/**
 * Calculate priority for attack move
 */
function calculateAttackPriority(
  attacker: UnitSnapshot,
  target: UnitSnapshot,
  expectedDamage: number,
  canKill: boolean,
  snapshot: GameStateSnapshot
): number {
  let priority = 100; // Base attack priority

  // Killing blows are highest priority
  if (canKill) {
    priority += 500;
    priority += UNIT_VALUES[target.category] / 10; // Higher value targets
  }

  // Low health targets
  const healthPercent = target.health / target.maxHealth;
  if (healthPercent < 0.3) priority += 200;
  else if (healthPercent < 0.5) priority += 100;

  // High-value targets
  if (target.category === UnitCategory.ARTILLERY) priority += 150;
  if (target.category === UnitCategory.ARMOR) priority += 100;

  // Damage dealt
  priority += expectedDamage * 5;

  // Type matchup bonus
  priority += getTypeMatchupBonus(attacker.category, target.category);

  // Focus fire (other units can also attack)
  const allies = getUnitsForFaction(snapshot, snapshot.currentFaction);
  const othersInRange = allies.filter(a =>
    a.id !== attacker.id &&
    !a.hasAttacked &&
    hexDistance(a.position, target.position) <= a.range
  );
  if (othersInRange.length >= 2) priority += 100;

  // CRITICAL: Huge bonus for attacking enemies ON or NEAR our cities!
  // These enemies are blocking production or threatening to capture
  const ourCities = snapshot.cities.filter(c => c.faction === snapshot.currentFaction);
  for (const city of ourCities) {
    // Enemy ON our city - blocks production and threatens capture
    if (isPositionOnCity(target.position, city)) {
      // EXTRA bonus if city is being captured - MUST stop the capture!
      // ALL units can capture, so any enemy on city is a critical threat
      if (city.isBeingCaptured) {
        priority += 800;  // MASSIVE bonus - stop the capture!
      } else {
        priority += 500;  // Big bonus - remove the threat (any unit can capture!)
      }
      break;
    }
    // Enemy near our city - threatens it
    const distToCity = distanceToCity(target.position, city);
    if (distToCity <= 3) {
      priority += 200;  // Big bonus for removing threats
      break;
    } else if (distToCity <= 6) {
      priority += 100;  // Moderate bonus
      break;
    }
  }

  // GARRISON/DEFENSE BONUS: Extra bonus for attacking enemies near defended city
  const attackerFrontType = snapshot.unitFrontTypes?.get(attacker.id);
  if (attackerFrontType === FrontType.GARRISON || attackerFrontType === FrontType.DEFENSE) {
    const objective = snapshot.unitObjectives?.get(attacker.id);
    if (objective) {
      const defendedCity = ourCities.find(c =>
        isPositionOnCity(objective, c) ||
        (c.centerPosition.x === objective.x && c.centerPosition.y === objective.y)
      );
      if (defendedCity) {
        const enemyDistToCity = distanceToCity(target.position, defendedCity);
        if (enemyDistToCity <= 2) {
          priority += 300;  // Huge bonus for defending city!
        } else if (enemyDistToCity <= 4) {
          priority += 150;  // Good bonus for nearby threats
        }
      }
    }
  }

  return priority;
}

/**
 * Calculate priority for movement
 */
function calculateMovePriority(
  unit: UnitSnapshot,
  destination: Position,
  snapshot: GameStateSnapshot,
  map: GameMap
): number {
  let priority = 50; // Base move priority

  // === GARRISON/DEFENSE PRIORITY ADJUSTMENT ===
  // Note: Move generation already filters out invalid moves for defended cities
  // This section adds priority bonuses/penalties for moves that ARE generated
  const unitFrontType = snapshot.unitFrontTypes?.get(unit.id);
  if (unitFrontType === FrontType.GARRISON || unitFrontType === FrontType.DEFENSE) {
    const objective = snapshot.unitObjectives?.get(unit.id);
    if (objective) {
      const ourCities = snapshot.cities.filter(c => c.faction === snapshot.currentFaction);
      const defendedCity = ourCities.find(c =>
        isPositionOnCity(objective, c) ||
        (c.centerPosition.x === objective.x && c.centerPosition.y === objective.y)
      );

      if (defendedCity) {
        const currentlyOnCity = isPositionOnCity(unit.position, defendedCity);
        const destOnCity = isPositionOnCity(destination, defendedCity);

        // Bonus for staying on defended city
        if (currentlyOnCity && destOnCity) {
          priority += 100;  // Good - staying on duty
        }
        // If not on city yet, bonus for moving toward it
        else if (!currentlyOnCity && destOnCity) {
          priority += 500;  // Big bonus for getting onto defended city
        }
        // If not on city, bonus/penalty for moving toward/away
        else if (!currentlyOnCity) {
          const currentDist = distanceToCity(unit.position, defendedCity);
          const newDist = distanceToCity(destination, defendedCity);
          if (newDist < currentDist) {
            priority += 200;  // Bonus for moving toward defended city
          } else if (newDist > currentDist) {
            priority -= 150;  // Penalty for moving away
          }
        }
      }
    }
  }

  // Infantry can capture cities - give them strong incentive to move toward cities
  const isInfantry = unit.category === UnitCategory.INFANTRY;

  // Transports carrying units should prioritize moving toward objectives
  const isLoadedTransport = (unit.category === UnitCategory.TRANSPORT || unit.category === UnitCategory.HALFTRACK) && unit.isCarryingUnit;

  // Check if unit is currently on one of our cities
  const ourCities = snapshot.cities.filter(c => c.faction === snapshot.currentFaction);
  const ourUnits = getUnitsForFaction(snapshot, snapshot.currentFaction);
  const enemyUnits = getUnitsForFaction(snapshot, snapshot.enemyFaction);
  const currentlyOnOurCity = ourCities.some(c => isPositionOnCity(unit.position, c));

  // Check if destination is on one of our cities (we want to LEAVE our cities!)
  const destOnOurCity = ourCities.some(c => isPositionOnCity(destination, c));

  // Find closest enemy or neutral city and move toward it
  const targetCities = snapshot.cities.filter(c =>
    c.faction === snapshot.enemyFaction || c.faction === null
  );

  // SMART CITY LEAVING: Only give bonus for leaving if city is safe to leave
  // BUT always allow leaving to ATTACK nearby enemies!
  if (currentlyOnOurCity && !destOnOurCity) {
    // Find which city we're on
    const cityWeAreOn = ourCities.find(c => isPositionOnCity(unit.position, c));

    if (cityWeAreOn) {
      // Count other defenders ON this city (besides us)
      const otherDefendersOnCity = ourUnits.filter(u =>
        u.id !== unit.id && isPositionOnCity(u.position, cityWeAreOn)
      ).length;

      // Count enemies nearby (within attack range or close enough to be a threat)
      const enemiesNearby = enemyUnits.filter(u =>
        distanceToCity(u.position, cityWeAreOn) <= 6
      );

      // Is city upgrading? (can't produce defenders)
      const cityIsUpgrading = cityWeAreOn.isUpgrading;

      // Check if this move is toward an enemy (to attack them)
      // If moving closer to a nearby enemy, this is likely an attack move - ALLOW IT!
      let isMovingToAttack = false;
      for (const enemy of enemiesNearby) {
        const currentDistToEnemy = hexDistance(unit.position, enemy.position);
        const newDistToEnemy = hexDistance(destination, enemy.position);
        // If we're getting closer to an enemy AND will be in attack range, this is an attack move
        if (newDistToEnemy < currentDistToEnemy && newDistToEnemy <= unit.range + 1) {
          isMovingToAttack = true;
          break;
        }
      }

      // === DETERMINE IF WE CAN LEAVE ===
      let canLeaveCity = true;
      let leaveBonus = 200;  // Default bonus for leaving to pursue objectives

      // ALWAYS allow leaving to attack nearby enemies!
      if (isMovingToAttack) {
        canLeaveCity = true;
        leaveBonus = 300;  // BONUS for attacking enemies near our city!
      }
      // CRITICAL: If city is upgrading and we're the ONLY defender, DON'T wander away
      else if (cityIsUpgrading && otherDefendersOnCity === 0) {
        canLeaveCity = false;
        // Only log once per city per turn to reduce spam
      }
      // If enemies are nearby and we're the only defender, don't wander away
      else if (enemiesNearby.length > 0 && otherDefendersOnCity === 0) {
        canLeaveCity = false;
      }
      // If city is upgrading but has other defenders, reduced bonus
      else if (cityIsUpgrading) {
        leaveBonus = 50;  // Reduced bonus - be cautious about leaving upgrading cities
      }
      // If enemies nearby but multiple defenders, small bonus
      else if (enemiesNearby.length > 0 && otherDefendersOnCity >= 1) {
        leaveBonus = 100;  // Reduced bonus
      }

      if (canLeaveCity && targetCities.length > 0) {
        priority += leaveBonus;  // Incentive to leave captured cities (when safe)
      } else if (!canLeaveCity) {
        priority -= 300;  // Penalty for wandering away from undefended city (reduced from 500)
      }
    } else if (targetCities.length > 0) {
      // Fallback if city not found
      priority += 200;
    }
  }

  // Check if unit has an assigned objective from FrontManager
  const assignedObjective = snapshot.unitObjectives?.get(unit.id);

  if (targetCities.length > 0 || assignedObjective) {
    // Use assigned objective if available, otherwise find closest city
    let targetPosition: Position;
    let assignedCity: CitySnapshot | null = null;

    if (assignedObjective) {
      // Unit has a specific objective - use that EXCLUSIVELY
      targetPosition = assignedObjective;
      // Find the city at that objective (if any)
      assignedCity = targetCities.find(c =>
        isPositionOnCity(assignedObjective, c) ||
        (c.centerPosition.x === assignedObjective.x && c.centerPosition.y === assignedObjective.y)
      ) || null;
    } else {
      // No assigned objective - find closest city
      assignedCity = targetCities[0];
      let closestDist = distanceToCity(unit.position, assignedCity);
      for (const city of targetCities) {
        const dist = distanceToCity(unit.position, city);
        if (dist < closestDist) {
          closestDist = dist;
          assignedCity = city;
        }
      }
      targetPosition = assignedCity.centerPosition;
    }

    // Calculate distances to ASSIGNED target
    const currentDist = assignedObjective
      ? hexDistance(unit.position, targetPosition)
      : distanceToCity(unit.position, assignedCity!);
    const newDist = assignedObjective
      ? hexDistance(destination, targetPosition)
      : distanceToCity(destination, assignedCity!);

    // Check if destination is ON the assigned city (any tile)
    const isOnAssignedCity = assignedCity ? isPositionOnCity(destination, assignedCity) : false;

    // CRITICAL: If unit has assigned objective, PENALIZE moves toward OTHER cities
    // This prevents units from being redirected to different objectives
    if (assignedObjective && !isOnAssignedCity) {
      for (const city of targetCities) {
        if (assignedCity && city.name === assignedCity.name) continue;  // Skip assigned city

        const currentDistToOther = distanceToCity(unit.position, city);
        const newDistToOther = distanceToCity(destination, city);

        // If this move gets us closer to a DIFFERENT city, penalize it!
        if (newDistToOther < currentDistToOther) {
          // HEAVY penalty for moving toward wrong objective
          priority -= 300;
          break;  // One penalty is enough
        }
      }
    }

    if (isOnAssignedCity) {
      // HUGE bonus for moving onto the ASSIGNED city tile
      priority += isInfantry ? 1000 : 300;

      // Loaded transports should also want to get close to cities (to unload)
      if (isLoadedTransport) {
        priority += 400;
      }

      // Extra bonus for neutral cities (easier to capture)
      if (assignedCity && assignedCity.faction === null) {
        priority += 300;
      }
    } else if (newDist < currentDist) {
      // MASSIVE bonus for getting closer to ASSIGNED objective - this is the PRIMARY goal
      const distReduction = currentDist - newDist;

      // Base bonus per hex - MUCH higher to ensure objective-seeking dominates
      // Far targets (>10 hexes): 150 points per hex closer
      // Medium (5-10 hexes): 120 points per hex
      // Close (<5 hexes): 100 points per hex
      let perHexBonus: number;
      if (currentDist > 10) {
        perHexBonus = 150;
      } else if (currentDist > 5) {
        perHexBonus = 120;
      } else {
        perHexBonus = 100;
      }

      priority += distReduction * perHexBonus;

      // Loaded transports get extra bonus
      if (isLoadedTransport) {
        priority += distReduction * 60;
      }

      // Extra bonus for neutral cities (easier to capture)
      if (assignedCity && assignedCity.faction === null) {
        priority += 80;
      }
    } else if (newDist === currentDist) {
      // LATERAL MOVE - doesn't get us closer! Heavy penalty to prevent shuffling
      priority -= 200;
    } else {
      // Moving AWAY from assigned objective - VERY heavy penalty
      priority -= (newDist - currentDist) * 150;
    }
  } else {
    // No target cities left - move toward enemies instead
    const enemies = getUnitsForFaction(snapshot, snapshot.enemyFaction);
    if (enemies.length > 0) {
      // Find closest enemy
      let closestEnemy = enemies[0];
      let closestDist = hexDistance(unit.position, closestEnemy.position);
      for (const enemy of enemies) {
        const dist = hexDistance(unit.position, enemy.position);
        if (dist < closestDist) {
          closestDist = dist;
          closestEnemy = enemy;
        }
      }

      const currentDist = closestDist;
      const newDist = hexDistance(destination, closestEnemy.position);

      if (newDist < currentDist) {
        priority += (currentDist - newDist) * 60;
      } else if (newDist === currentDist) {
        priority -= 50; // Lateral move penalty
      } else {
        priority -= (newDist - currentDist) * 40;
      }
    }
  }

  // Getting into attack range of enemies is valuable
  const enemies = getUnitsForFaction(snapshot, snapshot.enemyFaction);
  for (const enemy of enemies) {
    const currentDist = hexDistance(unit.position, enemy.position);
    const newDist = hexDistance(destination, enemy.position);

    // Getting into attack range is valuable
    if (newDist <= unit.range && currentDist > unit.range) {
      priority += 150; // Moved into attack range
    } else if (newDist < currentDist && newDist <= unit.range + 2) {
      priority += (currentDist - newDist) * 20;
    }
  }

  // Defensive terrain bonus - REDUCED to prevent overriding objectives
  const tile = map.getTile(destination.x, destination.y);
  if (tile) {
    const terrainData = TERRAIN_DATA[tile.terrain];
    if (terrainData) {
      // Only small bonus - objectives matter more than terrain
      priority += terrainData.defenseBonus * 5;
    }
  }

  // Smart ally clustering - units heading to same objective should stick together
  const allies = getUnitsForFaction(snapshot, snapshot.currentFaction);
  const nearbyAllies = allies.filter(a =>
    a.id !== unit.id &&
    hexDistance(destination, a.position) <= 2
  );

  // Check if we share objectives with nearby allies (from FrontManager)
  const ourObjective = snapshot.unitObjectives?.get(unit.id);
  for (const ally of nearbyAllies) {
    const allyObjective = snapshot.unitObjectives?.get(ally.id);
    if (ourObjective && allyObjective &&
        ourObjective.x === allyObjective.x && ourObjective.y === allyObjective.y) {
      // Same objective - combined arms is good!
      priority += 8;
    } else {
      // Different objectives - we should split up, slight penalty
      priority -= 3;
    }
  }

  // IMPORTANT: HEAVILY penalize staying on our own city when there are objectives
  if (currentlyOnOurCity && targetCities.length > 0 && destOnOurCity) {
    priority -= 500;
  }

  // Bonus for using more movement - encourages full movement usage
  const distanceMoved = hexDistance(unit.position, destination);
  if (distanceMoved > 0) {
    priority += Math.min(distanceMoved * 8, 30);
  }

  return priority;
}

/**
 * Calculate priority for move-and-attack
 */
function calculateMoveAttackPriority(
  attacker: UnitSnapshot,
  position: Position,
  target: UnitSnapshot,
  expectedDamage: number,
  canKill: boolean,
  snapshot: GameStateSnapshot,
  map: GameMap
): number {
  // Combine attack and move priorities
  const attackPriority = calculateAttackPriority(attacker, target, expectedDamage, canKill, snapshot);
  const movePriority = calculateMovePriority(attacker, position, snapshot, map);

  // Move-attack is slightly less preferable than attack-in-place (uses movement)
  return attackPriority + movePriority * 0.5 - 20;
}

/**
 * Calculate priority for building a unit
 */
function calculateBuildPriority(
  unitType: typeof import('../../../types').UnitType[keyof typeof import('../../../types').UnitType],
  snapshot: GameStateSnapshot,
  isLateGame: boolean = false
): number {
  const def = UNIT_DEFINITIONS[unitType];
  let priority = 30; // Base build priority

  // Analyze current composition
  const ourUnits = getUnitsForFaction(snapshot, snapshot.currentFaction);
  const composition = analyzeComposition(ourUnits);

  // Target composition - SWAPS in late game!
  // Early game: Infantry 40%, Armor 25% (infantry is cheap and plentiful)
  // Late game: Infantry 25%, Armor 40% (armor dominates against armor waves)
  const infantryTarget = isLateGame ? 0.25 : 0.4;
  const armorTarget = isLateGame ? 0.4 : 0.25;
  const artilleryTarget = 0.15;  // Artillery stays the same

  // Prioritize what we're lacking
  if (def.category === UnitCategory.INFANTRY && composition.infantry < infantryTarget) {
    priority += 50;
  }
  if (def.category === UnitCategory.ARMOR && composition.armor < armorTarget) {
    priority += 60;
  }
  if (def.category === UnitCategory.ARTILLERY && composition.artillery < artilleryTarget) {
    priority += 40;
  }

  // Higher priority if we have few units
  if (ourUnits.length < 5) {
    priority += 30;
  }

  return priority;
}

/**
 * Analyze army composition
 */
function analyzeComposition(units: UnitSnapshot[]): { infantry: number; armor: number; artillery: number } {
  if (units.length === 0) return { infantry: 0, armor: 0, artillery: 0 };

  let infantry = 0, armor = 0, artillery = 0;

  for (const unit of units) {
    if (unit.category === UnitCategory.INFANTRY) infantry++;
    if (unit.category === UnitCategory.ARMOR) armor++;
    if (unit.category === UnitCategory.ARTILLERY) artillery++;
  }

  const total = units.length;
  return {
    infantry: infantry / total,
    armor: armor / total,
    artillery: artillery / total
  };
}

/**
 * Get available units for a city based on its level and faction
 * Uses CATEGORY-BASED logic matching the actual game rules in cityUtils.canCityTrainUnit
 *
 * City Level Requirements (by category):
 * - OUTPOST (1): Infantry (non-airborne), Engineers, Medics
 * - FACTORY (2): + Armor, Artillery, Reconnaissance, Halftracks, Transports
 * - AIRBASE (3): + Aircraft, Airborne units
 */
function getAvailableUnitsForCity(city: CitySnapshot, faction: FactionType, campaign?: string): UnitType[] {
  // Get all units available for this faction in this campaign
  const allUnits = getUnitsForFactionFromDefs(
    faction,
    campaign as 'normandy' | 'tunisia' | 'stalingrad' | 'okinawa' | 'campobasso' | 'tutorial' | 'france_1940' | 'albania_1940' | undefined
  );

  // Filter by what this city level can train (using CATEGORY-based logic)
  return allUnits.filter(unitType => {
    const def = UNIT_DEFINITIONS[unitType];
    if (!def) return false;

    const category = def.category;

    // Aircraft and airborne units require Level 3 (Airbase) - CHECK THIS FIRST
    // Airborne units have category INFANTRY but still need an airbase
    if (isAirborneUnit(unitType) || isAircraft(unitType)) {
      const allowed = city.level === CityLevel.AIRBASE;
      if (!allowed) {
        // Debug: log when airborne/aircraft is blocked
        // console.log(`[V3 MoveGen] Blocked ${unitType} at ${city.name} (level ${city.level}) - requires AIRBASE`);
      }
      return allowed;
    }

    // Level 1 (Outpost) - Only basic infantry (non-airborne), engineers, medics
    if (city.level === CityLevel.OUTPOST) {
      return category === UnitCategory.INFANTRY ||
             category === UnitCategory.ENGINEERS ||
             category === UnitCategory.MEDICS;
    }

    // Level 2 (Factory) - Add tanks, artillery, reconnaissance, halftracks, transports
    if (city.level === CityLevel.FACTORY) {
      return category === UnitCategory.INFANTRY ||
             category === UnitCategory.ENGINEERS ||
             category === UnitCategory.MEDICS ||
             category === UnitCategory.ARMOR ||
             category === UnitCategory.ARTILLERY ||
             category === UnitCategory.RECONNAISSANCE ||
             category === UnitCategory.HALFTRACK ||
             category === UnitCategory.TRANSPORT;
    }

    // Level 3 (Airbase) - Can train everything
    if (city.level === CityLevel.AIRBASE) {
      return true;
    }

    return false;
  });
}

/**
 * Check if unit is defending an ally
 */
function isDefendingAlly(attacker: UnitSnapshot, target: UnitSnapshot, snapshot: GameStateSnapshot): boolean {
  const allies = getUnitsForFaction(snapshot, snapshot.currentFaction);
  return allies.some(a =>
    a.id !== attacker.id &&
    hexDistance(a.position, target.position) <= target.range
  );
}

/**
 * Check if position threatens a city
 */
function isThreatToCity(pos: Position, snapshot: GameStateSnapshot): boolean {
  return snapshot.cities.some(c =>
    c.faction === snapshot.enemyFaction &&
    distanceToCity(pos, c) <= 3
  );
}

/**
 * Check if moving to position improves tactical situation
 */
function isPositionImprovement(
  unit: UnitSnapshot,
  pos: Position,
  _snapshot: GameStateSnapshot,  // Reserved for checking enemy proximity
  map: GameMap
): boolean {
  const currentTile = map.getTile(unit.position.x, unit.position.y);
  const newTile = map.getTile(pos.x, pos.y);

  if (!currentTile || !newTile) return false;

  const currentDefense = TERRAIN_DATA[currentTile.terrain]?.defenseBonus ?? 0;
  const newDefense = TERRAIN_DATA[newTile.terrain]?.defenseBonus ?? 0;

  return newDefense > currentDefense;
}

/**
 * Get type matchup bonus
 */
function getTypeMatchupBonus(attackerCategory: UnitCategory, targetCategory: UnitCategory): number {
  // Armor beats infantry
  if (attackerCategory === UnitCategory.ARMOR && targetCategory === UnitCategory.INFANTRY) {
    return 30;
  }
  // Infantry beats artillery
  if (attackerCategory === UnitCategory.INFANTRY && targetCategory === UnitCategory.ARTILLERY) {
    return 25;
  }
  // Artillery is good against clusters (already factored elsewhere)
  if (attackerCategory === UnitCategory.ARTILLERY) {
    return 20;
  }
  return 0;
}

/**
 * Calculate move flags
 */
function calculateMoveFlags(
  unit: UnitSnapshot,
  pos: Position,
  snapshot: GameStateSnapshot,
  map: GameMap
): MoveFlags {
  const enemies = getUnitsForFaction(snapshot, snapshot.enemyFaction);
  const nearbyEnemiesAtOld = enemies.filter(e => hexDistance(unit.position, e.position) <= e.range);
  const nearbyEnemiesAtNew = enemies.filter(e => hexDistance(pos, e.position) <= e.range);

  return {
    isKillingBlow: false,
    threatensCity: isThreatToCity(pos, snapshot),
    capturesCity: snapshot.cities.some(c =>
      c.faction !== snapshot.currentFaction &&
      isPositionOnCity(pos, c)  // Check any city tile, not just center
    ),
    retreatsToSafety: nearbyEnemiesAtNew.length < nearbyEnemiesAtOld.length,
    supportsAlly: false,
    improvesPosition: isPositionImprovement(unit, pos, snapshot, map)
  };
}

/**
 * Create empty flags
 */
function createEmptyFlags(): MoveFlags {
  return {
    isKillingBlow: false,
    threatensCity: false,
    capturesCity: false,
    retreatsToSafety: false,
    supportsAlly: false,
    improvesPosition: false
  };
}

/**
 * Order moves by priority (highest first) for optimal alpha-beta pruning
 */
export function orderMoves(moves: GeneratedMove[]): GeneratedMove[] {
  return moves.sort((a, b) => {
    // Killing blows first
    if (a.flags.isKillingBlow && !b.flags.isKillingBlow) return -1;
    if (b.flags.isKillingBlow && !a.flags.isKillingBlow) return 1;

    // Then by priority score
    return b.priority - a.priority;
  });
}

/**
 * Limit moves to top N (for search efficiency)
 * CRITICAL: Always preserve ALL attacks - units should never skip attacks!
 */
export function limitMoves(moves: GeneratedMove[], maxMoves: number): GeneratedMove[] {
  // Keep ALL attacks (not just killing blows) - attacks should NEVER be filtered!
  const attacks = moves.filter(m =>
    m.type === MoveType.ATTACK ||
    m.type === MoveType.MOVE_AND_ATTACK ||
    m.flags.isKillingBlow
  );
  const nonAttacks = moves.filter(m =>
    m.type !== MoveType.ATTACK &&
    m.type !== MoveType.MOVE_AND_ATTACK &&
    !m.flags.isKillingBlow
  );

  const orderedNonAttacks = orderMoves(nonAttacks);
  const limitedNonAttacks = orderedNonAttacks.slice(0, Math.max(0, maxMoves - attacks.length));

  return orderMoves([...attacks, ...limitedNonAttacks]);
}
